/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  Text, 
  HStack, 
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
 } from '@chakra-ui/react';
import { CQReactTable, DotMenu, PrimaryButton } from '@laxmimanogna/code-quick-components';

import { createColumnHelper } from '@tanstack/react-table';
import useCQMediaQuery from '../../../../hooks/useCQMediaQuery';

import {
  createTableProps,
  paginationProps,
} from '../../../../utils/team.utils';
import { useNavigate } from 'react-router-dom';
import AppColors from '../../../../constants/AppColors';
import { DotMenuIcon } from '../../../../constants/IconData';
import { TeamContext } from '../../../../providers/TeamProvider';
import { toTitleCase } from '../../../../utils/common.utils';
import ROUTES from '../../../../constants/Routes';
import { replaceRoute } from '../../../../utils/common.utils';

const columnHelper = createColumnHelper();

const ReviewerTable = ({ setIsOpen, setCurrentTeam }) => {
  const teamContext = useContext(TeamContext);
  const navigate = useNavigate();
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  const { isUpdating, getReviewer, filterParams, handleApplyFilter } =
    teamContext;
  
  // SCROLL CHANGE: Auto-restore
  const PAGE_NAME = "Reviewer";
  const TABLE_NAME = "Reviewer Table";

  useEffect(() => {
    if (teamContext.reviewerList.length > 0) {
      const itemId = getReviewer.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
      if (itemId) {
        setHighlightedRowId(itemId);
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
    }
  }, [teamContext.reviewerList]);

  // media query
  const canScroll = useCQMediaQuery({
    lg: false,
    md: false,
    sm: true,
  });

  useEffect(() => {
    let searching = !!(filterParams.searchText && filterParams.searchText.trim());
    getReviewer.loadAPI("Reviewer", "Reviewer Table", searching);
  }, [filterParams]);

  const renderSpecialtyValue = specialty => {
    // eslint-disable-next-line array-callback-return
    const specialtyLabel = specialty.slice(0, 2).map(s => {
      if (s.name === 'ent') {
        return 'ENT';
      }
      if (s.name !== null || s.id !== null) {
        return toTitleCase(s.name);
      }
    });
    // eslint-disable-next-line array-callback-return
    const showSpecialities = specialty?.slice(2, specialty.length).map(s => {
      if (s.name === 'ent') {
        return 'ENT';
      } else if (s.name !== null || s.id !== null) {
        return s.name;
      }
    });
    return (
      <>
        <HStack>
          <Text fontSize={'16px'}>{specialtyLabel.join(', ')}</Text>

          {specialty.length > 2 ? (
            <>
              <Text>+</Text>
              <Tooltip
                hasArrow
                label={showSpecialities.join(' | ')}
                bg={AppColors.secondary}
                border={'none'}
              >
                <Text
                  bg={AppColors.secondary}
                  color={'#fff'}
                  px={2}
                  cursor={'pointer'}
                  py={1}
                  borderRadius={'full'}
                >
                  {specialty.length - 2}
                </Text>
              </Tooltip>
            </>
          ) : (
            ''
          )}
        </HStack>
      </>
    );
    //
  };

  const updateUserStatus = (userId, newStatus) => {
    teamContext.setReviewerList(prevData =>
      prevData.map(user =>
        user.id === userId
          ? { ...user, status: newStatus }
          : user
      )
    );
  };


  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: () => 'Name',
        cell: info => {
          const cellValue = info.getValue();
          return (
            <>
              {info.row.original?.teams?.length > 0 ? (
                <Popover placement="right-start">
                  <PopoverTrigger>
                    <Text
                      alignItems="self-start"
                      fontWeight="bold"
                      color={AppColors.secondary}
                      mt="2"
                      cursor="pointer"
                      _hover={{ textDecoration: "underline" }}
                    >
                      {toTitleCase(cellValue)}
                    </Text>
                  </PopoverTrigger>

                  <PopoverContent
                    w="200px"
                    p="2"
                    borderRadius="md"
                    boxShadow="lg"
                    bg="white"
                    _dark={{ bg: "gray.800" }}
                  >
                    <PopoverBody
                      maxH="180px"
                      overflowY="auto"
                      css={{
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#c1c1c1",
                          borderRadius: "3px",
                        },
                      }}
                    >
                      {info.row.original?.teams?.map((team) => {
                        const USER_PROFILE_ROUTE = replaceRoute(ROUTES.USER_PROFILE, {
                          teamId: team.id,
                          userId: info.row.original?.id,
                        });

                        return (
                          <Box
                            key={team.id}
                            cursor="pointer"
                            py="1"
                            px="2"
                            borderRadius="md"
                            _hover={{
                              bg: "gray.100",
                              _dark: { bg: "gray.700" },
                              textDecoration: "underline",
                              color: AppColors.secondary,
                            }}
                            onClick={() => {
                              navigate(USER_PROFILE_ROUTE, {
                                state: { Reviewer: true },
                              });
                              getReviewer.saveNavigationState(
                                PAGE_NAME,
                                TABLE_NAME,
                                info.row.original?.id,
                              );
                            }}
                          >
                            {toTitleCase(team.name)}
                          </Box>
                        );
                      })}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : (
                <Text
                  alignItems="self-start"
                  fontWeight="bold"
                  color={AppColors.secondary}
                  mt="2"
                >
                  {toTitleCase(cellValue)}
                </Text>
              )}
            </>
          );
        },
      }),
      columnHelper.accessor('email', {
        id: 'email',
        header: () => <span>Email</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (cellValue === null) {
            return 'n/a';
          }

          return (
            <Text fontWeight={'normal'} color={AppColors.secondary} textAlign="left">
              {cellValue}
            </Text>
          );
        },
      }),
      columnHelper.accessor('role', {
        id: 'role',
        header: () => <span>Role</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'n/a';
          }

          return (
            <Text
              alignItems={'self-start'}
              fontWeight={'bold'}
              color={AppColors.secondary}
              mt="2"
              textAlign="left"
            >
              {toTitleCase(cellValue)}
            </Text>
          );
        },
      }),

      columnHelper.accessor('teams', {
        id: 'teams',
        header: () => <span>Teams</span>,
        enableSorting: false,
        cell: info => {
          let specialty = info.row.original?.teams;
          if (specialty.length === 0) {
            return (
              <span style={{ display: 'flex', justifyContent: 'left' }}>
                N/A
              </span>
            );
          }
          return renderSpecialtyValue(specialty);
        },
      }),

      columnHelper.accessor('specialties', {
        header: () => 'Specialty',
        enableSorting: false,
        cell: info => {
          let specialty = info.row.original?.specialties;
          if (specialty.length === 0) {
            return (
              <span style={{ display: 'flex', justifyContent: 'left' }}>
                N/A
              </span>
            );
          }
          return renderSpecialtyValue(specialty);
        },
      }),

      columnHelper.accessor('status', {
        id: 'status',
        header: () => 'Status',
        enableSorting: false,
        cell: info => {
          const cellValue = info.getValue();
          if (cellValue === null) {
            return 'n/a';
          }

          return (
            <HStack>
              <Button
                color={AppColors.secondary}
                size="md"
                borderRadius="3xl"
                px={0}
                bg="transparent"
                outline="2px solid transparent"
                _hover={{}}
                _active={{}}
                pointerEvents="none"
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                textAlign="left"
                w="full"
              >
                {cellValue}
              </Button>
              <DotMenu
                icon={<DotMenuIcon w={1} />}
                menuDataItem={
                  cellValue === "Active"
                    ? [
                        {
                          onClick: () => {
                            navigate(
                              ROUTES.EDIT_USER.replace(
                                ":userId",
                                info.row.original?.id
                              ),
                              {state: {Reviewer: true}}
                            );
                          },
                          children: (
                            <div style={{ color: AppColors.black }}>Edit User</div>
                          ),
                        },
                        {
                          onClick: async () => {
                            const id = info.row.original?.id;
                            const success = await teamContext.toggleUserStatus(id);
                            if (success) {
                              updateUserStatus(id, "Inactive");
                            }
                          },
                          children: (
                            <div style={{ color: AppColors.red }}>Deactivate User</div>
                          ),
                        },
                      ]
                    : [
                        {
                          onClick: async () => {
                            const id = info.row.original?.id;
                            const success = await teamContext.toggleUserStatus(id);
                            if (success) {
                              updateUserStatus(id, "Active"); // Update locally
                            }
                          },
                          children: (
                            <div style={{ color: AppColors.green }}>Activate User</div>
                          ),
                        },
                      ]
                }
              />
            </HStack>
          );
        },
      }),
    ],
    [teamContext.isReviewerLoading]
  );

  function handlePreviousPage() {
    teamContext.getReviewer.loadPrevious("Reviewer", "Reviewer Table");
  }

  function handleNextPage() {
    teamContext.getReviewer.loadNext("Reviewer", "Reviewer Table");
  }

  function handleSizeChange(size) {
    teamContext.getReviewer.loadSize("Reviewer", "Reviewer Table", size );
  }

  function handlePageChangeByNumber(pageNumber) {
    teamContext.getReviewer.loadPageByNumber("Reviewer", "Reviewer Table", pageNumber);
  }

  return (
    <Box
      pointerEvents={isUpdating ? 'none' : 'inherit'}
      overflowX={canScroll ? 'scroll' : 'inherit'}
    >
      <CQReactTable
        isLoading={teamContext.isReviewerLoading}
        data={teamContext.reviewerList}
        columns={columns}
        {...createTableProps(highlightedRowId)}
        {...paginationProps(getReviewer)}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onSizeChange={handleSizeChange}
        onPageChangeByNumber={handlePageChangeByNumber}
      />
    </Box>
  );
};
export default ReviewerTable;
